import { Button, Card, Empty, Flex, Form, Input, message, Select } from "antd";
import { useForm } from "antd/es/form/Form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import WarehouseService from "../../services/Warehouses/WarehouseService";
import WarehouseReceivingZoneDelete from "./WarehouseReceivingZoneDelete";
import useWarehouseZoneTypes from "../../customHooks/useWarehouseZoneTypes";
import AppButton from "../../common/AppButton";

const WarehouseReceivingZoneForm = ({
  warehouseId,
  receivingZoneId,
  handleIsModifying,
  handleReceivingZoneIdChange,
}) => {
  const [form] = useForm();
  const queryClient = useQueryClient();

  const handleCancel = () => {
    form.resetFields();
    handleIsModifying();
    handleReceivingZoneIdChange(null);
  };

  const reset = async () => {
    await queryClient.invalidateQueries({ queryKey: [warehouseId, "receivingZones"] });
    await queryClient.invalidateQueries({ queryKey: [warehouseId, "docks"] });
    await queryClient.invalidateQueries({ queryKey: [warehouseId, "receivingZones", receivingZoneId] });
    handleCancel();
  };

  const { data: zoneTypes } = useWarehouseZoneTypes();

  const { data: docks } = useQuery({
    queryKey: [warehouseId, "docks"],
    queryFn: async () => await WarehouseService.getDocksByWarehouseId(warehouseId),
    enabled: !!warehouseId,
    select: (data) =>
      data.filter(dock => dock.receivingZoneId === +receivingZoneId || !dock.receivingZoneId),
  });

  const cachedReceivingZone = queryClient.getQueryData([warehouseId, "receivingZones"])
    ?.find(zone => zone.id === +receivingZoneId);
  if (cachedReceivingZone) {
    queryClient.setQueryData([warehouseId, "receivingZones", receivingZoneId], cachedReceivingZone);
  }
  const { data: receivingZone } = useQuery({
    queryKey: [warehouseId, "receivingZones", receivingZoneId],
    queryFn: async () => await WarehouseService.getReceivingZoneById(warehouseId, receivingZoneId),
    enabled: !cachedReceivingZone && !!receivingZoneId && zoneTypes.length > 0,
    select: data => {
      const { warehouseDocks, zoneType, ...receivingZoneRequest } = data;
      return {
        ...receivingZoneRequest,
        warehouseDockIds: warehouseDocks?.map(dock => dock.id),
        zoneTypeId: zoneType?.id,
      };
    },
  });

  const { mutate: addNewReceivingZones } = useMutation({
    mutationFn: async (request) => await WarehouseService.addNewReceivingZones(warehouseId, request),
    onSuccess: async () => {
      await reset();
      message.success("Receiving zone added successfully!");
    },
    onError: async (error) => {
      message.error(`Failed to add receiving zone. ${error.response?.data?.message}, Please try again.`);
    },
  });

  const { mutate: updateReceivingZone } = useMutation({
    mutationFn: async (request) => await WarehouseService.updateReceivingZone(warehouseId, receivingZoneId, request),
    onSuccess: async () => {
      await reset();
      message.success("Receiving zone updated successfully!");
    },
    onError: async (error) => {
      message.error(`Failed to update receiving zone. ${error.response?.data?.message}, Please try again.`);
    },
  });

  const zoneTypeId = Form.useWatch("zoneTypeId", form);
  let selectedZoneType = zoneTypes?.find(z => z.id === zoneTypeId);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (receivingZoneId) {
        updateReceivingZone(values);
      } else {
        addNewReceivingZones([values]);
      }
    } catch {
      // Do nothing, let the form handle the error messages
    }
  };

  const title = !receivingZoneId ? "Add Receiving Zone" : "Edit Receiving Zone Details";

  return (
    <Card title={title} className="card-form">
      <Form form={form}
            initialValues={receivingZone}
            layout={"vertical"}>
        <Flex justify="space-between" align="start" gap={8}>
          <Form.Item label="Zone Name"
                     rules={[{ required: true, message: "Please enter zone name" }]}
                     name="name">
            <Input size="large" />
          </Form.Item>
          <Form.Item label="Short Code"
                     normalize={(value) => value?.trim()}
                     rules={[
                       { required: true, message: "Please enter zone code" },
                       { max: 10, message: "Code must be 10 characters or less" },
                     ]}
                     name="code">
            <Input size="large" />
          </Form.Item>
        </Flex>
        <Form.Item label="Zone Type"
                   rules={[{ required: true, message: "Please select zone type" }]}
                   name="zoneTypeId">
          <Select size="large">
            {zoneTypes?.map(zoneType => (
              <Select.Option key={zoneType.id} value={zoneType.id}>{zoneType.name}</Select.Option>
            ))}
          </Select>
        </Form.Item>
        {selectedZoneType?.isDockingZone && (
          <Form.Item label="Docks"
                     rules={[
                       { required: true, message: "At least one dock must be selected" },
                     ]}
                     name="warehouseDockIds">
            <Select size="large" mode="multiple" placeholder="Select docks" notFoundContent={<Empty description="No Docks availble, please enter new Docks." />}>
              {docks?.map(dock => (
                <Select.Option key={dock.id} value={dock.id}>{dock.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
        <Form.Item label="Description" name="description">
          <Input.TextArea size="large" rows={2} />
        </Form.Item>
      </Form>
      <Flex justify="end" align="center" gap={8} className="mt-4">
        <AppButton variant="save" onClick={handleSubmit}>
                Save
        </AppButton>
        {receivingZoneId && <WarehouseReceivingZoneDelete id={receivingZoneId}
                                                          warehouseId={warehouseId}
                                                          handleCancel={handleCancel} />}
        <AppButton variant="close" onClick={handleCancel}>
                Close
        </AppButton>
      </Flex>
    </Card>
  );
};

export default WarehouseReceivingZoneForm;